import { MACROS, SETORES } from "@caint/data";
import { type Eixo, type Engine, type Momento, maturidadeDisparaPlano } from "@caint/engine";
import type { ItemCatalogo, MacroCodigo } from "@caint/shared";

/**
 * Análise do Relatório Executivo: agregações puras por setor / macroprocesso /
 * eixo / conformidade / risco e um gerador de ANÁLISE CRÍTICA (findings
 * estruturados) a partir dos números. Sem IO — mesma entrada, mesma saída.
 * Fonte única do "diagnóstico textual" do relatório (doc 05/04).
 *
 * As funções que dependem do motor recebem a instância `Engine` como PRIMEIRO
 * argumento (injeção), mantendo o pacote agnóstico à config (v4.0 ou editada).
 */

/** Limiares oficiais do motor (config v4.0): meta de subíndice e faixa Prata. */
export const META_SUBINDICE = 0.46;
export const META_PRATA = 0.6;
/** Risco residual que aciona a priorização de prazo do plano (gatilho do motor). */
export const GATILHO_PLANO = 8;

export type Severidade = "ok" | "atencao" | "critico";

const EIXOS: Eixo[] = ["IMGO", "IMQA", "IMGR"];
const macroNome = (m: string): string => MACROS[m as MacroCodigo] ?? m;

/* ----------------------------- por SETOR ----------------------------- */

export interface ResumoSetor {
  code: string;
  nome: string;
  macro: string;
  macroNome: string;
  /** Itens do setor no escopo (aplicáveis). */
  nItens: number;
  /** Itens já avaliados (com nota no momento). */
  nAvaliados: number;
  score: number | null;
  /** Itens CORE (peso 3) em maturidade 0 ou 1 — travam o selo. */
  coreCriticos: number;
  nc: number;
  na: number;
  /** Itens que entram no Plano de Ação (maturidade inicial 0–2). */
  noPlano: number;
}

export function porSetor(engine: Engine, items: readonly ItemCatalogo[], momento: Momento): ResumoSetor[] {
  const grupos = new Map<string, ItemCatalogo[]>();
  for (const it of items) {
    const arr = grupos.get(it.sector) ?? [];
    arr.push(it);
    grupos.set(it.sector, arr);
  }
  const out: ResumoSetor[] = [];
  for (const [code, its] of grupos) {
    const info = SETORES.find((s) => s.code === code);
    out.push({
      code,
      nome: info?.nome ?? code,
      macro: info?.macro ?? "",
      macroNome: macroNome(info?.macro ?? ""),
      nItens: its.length,
      nAvaliados: its.filter((it) => it[momento] != null).length,
      score: engine.setorScore(its, momento),
      coreCriticos: engine.contarCore(its, 0, momento) + engine.contarCore(its, 1, momento),
      nc: engine.contarConf(its, "NC", momento),
      na: engine.contarNA(its, momento),
      noPlano: its.filter((it) => maturidadeDisparaPlano(it[momento])).length,
    });
  }
  // Ordena do MENOR score ao maior (piores primeiro) — a UI re-ordena se quiser.
  return out.sort((a, b) => (a.score ?? Number.POSITIVE_INFINITY) - (b.score ?? Number.POSITIVE_INFINITY));
}

/* -------------------------- por MACROPROCESSO -------------------------- */

export interface ResumoMacro {
  macro: string;
  nome: string;
  score: number | null;
  nItens: number;
}

export function porMacro(engine: Engine, items: readonly ItemCatalogo[], momento: Momento): ResumoMacro[] {
  const grupos = new Map<string, ItemCatalogo[]>();
  for (const it of items) {
    const info = SETORES.find((s) => s.code === it.sector);
    const macro = info?.macro ?? "?";
    const arr = grupos.get(macro) ?? [];
    arr.push(it);
    grupos.set(macro, arr);
  }
  // Ordem canônica dos 5 macroprocessos (G,A,S,D,L) que existirem no escopo.
  return (Object.keys(MACROS) as MacroCodigo[])
    .filter((m) => grupos.has(m))
    .map((m) => {
      const its = grupos.get(m) ?? [];
      return { macro: m, nome: MACROS[m], score: engine.setorScore(its, momento), nItens: its.length };
    });
}

/* ------------------------------ por EIXO ------------------------------ */

export interface ResumoEixo {
  eixo: Eixo;
  score: number | null;
  /** Nº REAL de itens do eixo no escopo (corrige o hardcode 80/111/56). */
  nItens: number;
}

export function porEixo(engine: Engine, items: readonly ItemCatalogo[], momento: Momento): ResumoEixo[] {
  return EIXOS.map((eixo) => {
    const its = items.filter((it) => it.eixo === eixo);
    return { eixo, score: engine.subindice(its, eixo, momento), nItens: its.length };
  });
}

/* --------------------------- CONFORMIDADE ---------------------------- */

export interface DistConformidade {
  /** Curva ONA agregada. */
  nc: number;
  pc: number;
  c: number;
  cs: number;
  na: number;
  /** Itens por nível de maturidade observado (0–4). */
  niveis: Record<"0" | "1" | "2" | "3" | "4", number>;
  /** Itens CORE (peso 3) por nível — foco da trava. */
  coreNiveis: Record<"0" | "1" | "2" | "3" | "4", number>;
  totalAvaliado: number;
}

export function distConformidade(
  engine: Engine,
  items: readonly ItemCatalogo[],
  momento: Momento,
): DistConformidade {
  const niveis = { "0": 0, "1": 0, "2": 0, "3": 0, "4": 0 };
  const coreNiveis = { "0": 0, "1": 0, "2": 0, "3": 0, "4": 0 };
  let totalAvaliado = 0;
  for (const it of items) {
    const v = it[momento];
    if (v == null || v === "na") {
      continue;
    }
    if (v in niveis) {
      niveis[v as keyof typeof niveis]++;
      if (it.peso === 3) {
        coreNiveis[v as keyof typeof coreNiveis]++;
      }
      totalAvaliado++;
    }
  }
  return {
    nc: engine.contarConf(items, "NC", momento),
    pc: engine.contarConf(items, "PC", momento),
    c: engine.contarConf(items, "C", momento),
    cs: engine.contarConf(items, "CS", momento),
    na: engine.contarNA(items, momento),
    niveis,
    coreNiveis,
    totalAvaliado,
  };
}

/* --------------------------- PLANO DE AÇÃO --------------------------- */

/**
 * Nº de itens que entram no Plano de Ação (metodologia FACR): maturidade
 * inicial em 0–2. Usa a MESMA regra do motor (`maturidadeDisparaPlano`) e da
 * página de Plano de Ação — por isso o total do relatório bate com o do plano.
 * Nota: é diferente de "risco residual ≥ 8" (`distRisco`), que é priorização de
 * prazo, não critério de entrada no plano.
 */
export function contarPlano(items: readonly ItemCatalogo[], momento: Momento): number {
  let n = 0;
  for (const it of items) {
    if (maturidadeDisparaPlano(it[momento])) {
      n++;
    }
  }
  return n;
}

/* ------------------------------ RISCO -------------------------------- */

export interface PontoRisco {
  itemId: string;
  code: string;
  inerente: number;
  residual: number;
  noPlano: boolean;
}
export interface DistRisco {
  /** Contagem por nível de risco residual. */
  rp: number;
  rm: number;
  ra: number;
  rc: number;
  /** Itens no recorte de risco residual ≥ 8, por prazo (priorização). */
  plano15: number; // residual > 14 (RC) → 15 dias
  plano60: number; // 8 ≤ residual ≤ 14 → 60 dias
  pontos: PontoRisco[];
}

export function distRisco(engine: Engine, items: readonly ItemCatalogo[], momento: Momento): DistRisco {
  const d: DistRisco = { rp: 0, rm: 0, ra: 0, rc: 0, plano15: 0, plano60: 0, pontos: [] };
  for (const it of items) {
    const residual = engine.riscoResidual(it, momento);
    if (residual == null) {
      continue;
    }
    const nivel = engine.nivelRisco(residual);
    if (nivel === "RP") d.rp++;
    else if (nivel === "RM") d.rm++;
    else if (nivel === "RA") d.ra++;
    else if (nivel === "RC") d.rc++;
    const noPlano = residual >= GATILHO_PLANO;
    if (noPlano) {
      if (residual > 14) d.plano15++;
      else d.plano60++;
    }
    d.pontos.push({
      itemId: it.id,
      code: it.code,
      inerente: engine.riscoInerente(it),
      residual,
      noPlano,
    });
  }
  return d;
}

/* -------------------------- ANÁLISE CRÍTICA -------------------------- */

export interface Finding {
  dimensao: string;
  severidade: Severidade;
  titulo: string;
  texto: string;
}

export interface EntradaAnalise {
  items: readonly ItemCatalogo[];
  momento: Momento;
  imgq: {
    imgo: number | null;
    imqa: number | null;
    imgr: number | null;
    bruto: number;
    efetivo: number;
    ajustado: number;
    core0: number;
    core1: number;
    travado: boolean;
  };
  selo: { rotulo: string; nivel: string };
  unidadeNome: string;
}

const pct1 = (v: number | null): string => (v == null ? "—" : `${(v * 100).toFixed(1).replace(".", ",")}%`);
const nomeEixo: Record<Eixo, string> = {
  IMGO: "Gestão Operacional",
  IMQA: "Qualidade Assistencial",
  IMGR: "Gestão de Riscos",
};

/** Gera os findings da análise crítica agrupados por dimensão. Pura. */
export function analiseCritica(engine: Engine, e: EntradaAnalise): Finding[] {
  const f: Finding[] = [];
  const { imgq } = e;

  // --- Checklist / IMGQ geral ---
  const sevSelo: Severidade =
    imgq.efetivo >= META_PRATA ? "ok" : imgq.efetivo >= META_SUBINDICE ? "atencao" : "critico";
  f.push({
    dimensao: "Visão geral",
    severidade: sevSelo,
    titulo: `Selo ${e.selo.rotulo || "—"} · IMGQ ${pct1(imgq.efetivo)}`,
    texto: imgq.travado
      ? `Selo TRAVADO em ≤ 59,9% pela regra de exclusão CORE: há itens críticos em nível 0/1 ou eixo abaixo da meta (${pct1(META_SUBINDICE)}). Corrigir os CORE críticos destrava a evolução.`
      : `IMGQ efetivo em ${pct1(imgq.efetivo)} (bruto ${pct1(imgq.bruto)}). ${sevSelo === "ok" ? "Acima do patamar Prata." : sevSelo === "atencao" ? "Entre a meta e o Prata — há espaço claro de evolução." : "Abaixo da meta institucional de 46%."}`,
  });

  // --- Por eixo (IMGO/IMQA/IMGR) ---
  const eixos: [Eixo, number | null][] = [
    ["IMGO", imgq.imgo],
    ["IMQA", imgq.imqa],
    ["IMGR", imgq.imgr],
  ];
  const presentes = eixos.filter(([, v]) => v != null) as [Eixo, number][];
  if (presentes.length > 0) {
    const fragil = presentes.reduce((a, b) => (b[1] < a[1] ? b : a));
    const forte = presentes.reduce((a, b) => (b[1] > a[1] ? b : a));
    const abaixo = presentes.filter(([, v]) => v < META_SUBINDICE);
    f.push({
      dimensao: "Eixos",
      severidade: abaixo.length > 0 ? "critico" : fragil[1] < META_PRATA ? "atencao" : "ok",
      titulo: `Eixo mais frágil: ${fragil[0]} (${pct1(fragil[1])})`,
      texto: `${fragil[0]} — ${nomeEixo[fragil[0]]} é o subíndice mais baixo (${pct1(fragil[1])}); ${forte[0]} — ${nomeEixo[forte[0]]} o mais alto (${pct1(forte[1])}). ${
        abaixo.length > 0
          ? `${abaixo.length} eixo(s) abaixo da meta de 46% — cada um abaixo da meta trava o selo.`
          : "Todos os eixos acima da meta de 46%."
      }`,
    });
  }

  // --- Itens CORE ---
  const totalCore = e.items.filter((it) => it.peso === 3).length;
  const coreCriticos = imgq.core0 + imgq.core1;
  f.push({
    dimensao: "Itens CORE",
    severidade: coreCriticos > 0 ? "critico" : "ok",
    titulo: `${coreCriticos} CORE crítico(s) de ${totalCore}`,
    texto:
      coreCriticos > 0
        ? `${imgq.core0} item(ns) CORE em nível 0 e ${imgq.core1} em nível 1. Itens CORE (alta criticidade) em 0/1 acionam a exclusão e travam o selo em ≤ 59,9%.`
        : "Nenhum item CORE em nível 0/1 — a trava de exclusão não está acionada por CORE.",
  });

  // --- Não conformidades e setores frágeis ---
  const setores = porSetor(engine, e.items, e.momento);
  const abaixoMeta = setores.filter((s) => s.score != null && s.score < META_SUBINDICE);
  const totalNc = engine.contarConf(e.items, "NC", e.momento);
  f.push({
    dimensao: "Não conformidades",
    severidade: totalNc > 0 ? (totalNc > 10 ? "critico" : "atencao") : "ok",
    titulo: `${totalNc} não conformidade(s) (NC)`,
    texto:
      totalNc > 0
        ? `${totalNc} item(ns) avaliados como Não Conforme (maturidade 0/1). Concentre a ação nos setores abaixo da meta.`
        : "Sem itens Não Conformes no escopo avaliado.",
  });
  if (abaixoMeta.length > 0) {
    const piores = abaixoMeta.slice(0, 3).map((s) => `${s.code} ${s.nome} (${pct1(s.score)})`);
    f.push({
      dimensao: "Setores",
      severidade: "critico",
      titulo: `${abaixoMeta.length} setor(es) abaixo da meta`,
      texto: `Prioridade de intervenção: ${piores.join("; ")}${abaixoMeta.length > 3 ? "; …" : ""}.`,
    });
  }

  // --- Plano de Ação (maturidade 0–2) + priorização por risco ---
  const risco = distRisco(engine, e.items, e.momento);
  const nRisco = risco.plano15 + risco.plano60;
  const nPlano = contarPlano(e.items, e.momento);
  f.push({
    dimensao: "Plano de Ação",
    severidade: risco.plano15 > 0 ? "critico" : nPlano > 0 ? "atencao" : "ok",
    titulo: `${nPlano} item(ns) no Plano de Ação`,
    texto:
      nPlano > 0
        ? `${nPlano} item(ns) em maturidade inicial 0–2 exigem plano de ação formal. Priorização por risco residual: ${risco.plano15} em prazo de 15 dias (crítico) e ${risco.plano60} em 60 dias (alto)${nRisco < nPlano ? "; os demais em risco moderado/baixo" : ""}.`
        : "Nenhum item em maturidade inicial 0–2 — sem Plano de Ação obrigatório.",
  });

  return f;
}

/* --------------------- SIMULADOR DE META (peso CORE) --------------------- */

/** Score por nível 0..4 (curva ONA). */
const CURVA_NIVEL = [0, 0.2, 0.5, 0.8, 1];
const curva = (L: number): number => CURVA_NIVEL[L] ?? 0;

function nivelDe(mi: string | null | undefined): number | null {
  if (mi == null || mi === "na" || mi === "") return null;
  const L = Number(mi);
  return Number.isInteger(L) && L >= 0 && L <= 4 ? L : null;
}

export interface MatSim {
  readonly mi: string | null | undefined;
  readonly peso: number;
  readonly eixo: Eixo;
}
export interface ImgqSim {
  subs: Record<Eixo, number | null>;
  nItens: Record<Eixo, number>;
  bruto: number;
  efetivo: number;
  core0: number;
  core1: number;
  travado: boolean;
}

/** IMGQ com peso do CORE configurável (peso 3 = motor real). Espelha o engine. */
export function imgqComPeso(mats: readonly MatSim[], pesoCore: number): ImgqSim {
  const subs = { IMGO: null, IMQA: null, IMGR: null } as Record<Eixo, number | null>;
  const nItens = { IMGO: 0, IMQA: 0, IMGR: 0 } as Record<Eixo, number>;
  const num = { IMGO: 0, IMQA: 0, IMGR: 0 } as Record<Eixo, number>;
  const den = { IMGO: 0, IMQA: 0, IMGR: 0 } as Record<Eixo, number>;
  let core0 = 0;
  let core1 = 0;
  for (const it of mats) {
    if (it.peso === 3) {
      if (it.mi === "0") core0++;
      else if (it.mi === "1") core1++;
    }
    const L = nivelDe(it.mi);
    if (L === null) continue;
    const w = it.peso === 3 ? pesoCore : it.peso;
    num[it.eixo] += curva(L) * w;
    den[it.eixo] += w;
    nItens[it.eixo] += 1;
  }
  let bn = 0;
  let bd = 0;
  for (const e of EIXOS) {
    subs[e] = den[e] > 0 ? num[e] / den[e] : null;
    const s = subs[e];
    if (s != null) {
      bn += s * nItens[e];
      bd += nItens[e];
    }
  }
  const bruto = bd > 0 ? bn / bd : 0;
  const abaixo = EIXOS.some((e) => subs[e] != null && (subs[e] as number) < META_SUBINDICE);
  const travado = core0 > 0 || core1 > 0 || abaixo;
  const efetivo = travado ? Math.min(bruto, 0.599) : bruto;
  return { subs, nItens, bruto, efetivo, core0, core1, travado };
}

/** Score médio dos itens CORE vs. não-CORE (avaliados) — mostra se reduzir o peso do CORE subiria/desceria. */
export function mediaScoreClasse(items: readonly ItemCatalogo[], momento: Momento) {
  let cs = 0;
  let cn = 0;
  let ns = 0;
  let nn = 0;
  for (const it of items) {
    const L = nivelDe(it[momento]);
    if (L === null) continue;
    if (it.peso === 3) {
      cs += curva(L);
      cn++;
    } else {
      ns += curva(L);
      nn++;
    }
  }
  return { core: cn > 0 ? cs / cn : null, naoCore: nn > 0 ? ns / nn : null, nCore: cn, nNaoCore: nn };
}

export interface PassoPlano {
  itemId: string;
  code: string;
  eixo: Eixo;
  peso: number;
  de: number;
  para: number;
  ganhoPP: number;
  imgqApos: number;
}
export interface PlanoMeta {
  passos: PassoPlano[];
  jaBate: boolean;
  imgqInicial: number;
  imgqFinal: number;
  atingiu: boolean;
}

/**
 * Plano de MENOR ESFORÇO para o IMGQ atingir a meta: greedy que, a cada passo,
 * sobe 1 nível o item de maior ganho marginal. O ganho é forma fechada —
 * `Δscore · peso · nEixo / (denEixo · nTotal)` — porque o bruto é linear em cada
 * item; CORE (peso 3) e saltos 1→2 / 2→3 (Δ0,3) tendem a vir primeiro. Motor
 * real: pesoCore = 3. Só considera itens avaliados (não-NA) abaixo do nível 4.
 */
export function planoMeta(
  items: readonly ItemCatalogo[],
  momento: Momento,
  meta = META_SUBINDICE,
): PlanoMeta {
  const den = { IMGO: 0, IMQA: 0, IMGR: 0 } as Record<Eixo, number>;
  const nE = { IMGO: 0, IMQA: 0, IMGR: 0 } as Record<Eixo, number>;
  const num = { IMGO: 0, IMQA: 0, IMGR: 0 } as Record<Eixo, number>;
  const mats: { id: string; code: string; eixo: Eixo; peso: number; L: number }[] = [];
  for (const it of items) {
    const L = nivelDe(it[momento]);
    if (L === null) continue;
    const w = it.peso === 3 ? 3 : it.peso;
    den[it.eixo] += w;
    nE[it.eixo] += 1;
    num[it.eixo] += curva(L) * w;
    mats.push({ id: it.id, code: it.code, eixo: it.eixo, peso: it.peso, L });
  }
  const totalN = EIXOS.reduce((s, e) => s + nE[e], 0);
  const bruto = () => {
    let bn = 0;
    let bd = 0;
    for (const e of EIXOS) {
      if (den[e] > 0) {
        bn += (num[e] / den[e]) * nE[e];
        bd += nE[e];
      }
    }
    return bd > 0 ? bn / bd : 0;
  };
  const imgqInicial = imgqComPeso(
    items.map((it) => ({ mi: it[momento], peso: it.peso, eixo: it.eixo })),
    3,
  ).efetivo;
  if (imgqInicial >= meta || totalN === 0) {
    return {
      passos: [],
      jaBate: imgqInicial >= meta,
      imgqInicial,
      imgqFinal: imgqInicial,
      atingiu: imgqInicial >= meta,
    };
  }
  const passos: PassoPlano[] = [];
  let b = bruto();
  let guard = 0;
  while (b < meta && guard++ < 1000) {
    let best = -1;
    let bestGanho = 0;
    for (let i = 0; i < mats.length; i++) {
      const m = mats[i];
      if (!m || m.L >= 4) continue;
      const w = m.peso === 3 ? 3 : m.peso;
      const ganho = ((curva(m.L + 1) - curva(m.L)) * w * nE[m.eixo]) / (den[m.eixo] * totalN);
      if (ganho > bestGanho) {
        bestGanho = ganho;
        best = i;
      }
    }
    if (best < 0 || bestGanho <= 0) break;
    const m = mats[best];
    if (!m) break;
    const w = m.peso === 3 ? 3 : m.peso;
    num[m.eixo] += (curva(m.L + 1) - curva(m.L)) * w;
    m.L += 1;
    b = bruto();
    passos.push({
      itemId: m.id,
      code: m.code,
      eixo: m.eixo,
      peso: m.peso,
      de: m.L - 1,
      para: m.L,
      ganhoPP: bestGanho * 100,
      imgqApos: b,
    });
  }
  return { passos, jaBate: false, imgqInicial, imgqFinal: b, atingiu: b >= meta };
}
